import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationAPI } from '../services/api';
import { playNotificationBuzzer } from '../utils/notificationSound';

const POLL_INTERVAL = 30_000;

// `types` (optional array of Notification.type strings) scopes this bell to
// one portal's own notifications — a single User account can be both a
// passenger and transport-provider staff, and without this the bell would
// mix both roles' history together (see notificationTypes.js). Omit it to
// keep the original unscoped behavior (the admin/company portal's usage of
// this bell has no such leak to fix).
const NotificationBell = ({ dark = false, types }) => {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timerRef = useRef(null);
  const prevUnreadRef = useRef(null); // null = no baseline yet, skip buzzing on the first fetch
  const typesParam = types?.length ? types.join(',') : undefined;

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await notificationAPI.getAll(typesParam ? { types: typesParam } : undefined);
      const rows = res.data.data || [];
      setNotifications(rows);
      const unreadCount = rows.filter((n) => !n.isRead).length;
      if (prevUnreadRef.current !== null && unreadCount > prevUnreadRef.current) {
        playNotificationBuzzer();
      }
      prevUnreadRef.current = unreadCount;
    } catch {
      // silent — bell is non-critical
    }
  }, [typesParam]);

  useEffect(() => {
    fetchNotifications();
    timerRef.current = setInterval(fetchNotifications, POLL_INTERVAL);

    // Pause polling while the tab is hidden; resume and refresh immediately on visibility
    const handleVisibility = () => {
      if (document.hidden) {
        clearInterval(timerRef.current);
      } else {
        fetchNotifications();
        timerRef.current = setInterval(fetchNotifications, POLL_INTERVAL);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  const markRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await notificationAPI.markAllRead(typesParam ? { types: typesParam } : undefined);
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch { /* silent */ }
  };

  const textBase  = dark ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900';
  const badgeBg   = 'bg-red-500 text-white';

  // The buzzer fires the instant a notification arrives (via the separate
  // socket-push path in PassengerNotificationContext), but this bell's own
  // list is only as fresh as its last 30s poll — opening the dropdown right
  // after a buzz could show stale/empty data for up to that long otherwise.
  const toggleOpen = () => {
    setOpen((o) => {
      const next = !o;
      if (next) fetchNotifications();
      return next;
    });
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={toggleOpen}
        className={`relative p-2 rounded-lg transition ${textBase} hover:bg-white/10`}
        aria-label="الإشعارات"
      >
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className={`absolute top-1 right-1 min-w-[1.1rem] h-[1.1rem] text-[0.6rem] font-bold flex items-center justify-center rounded-full px-0.5 ${badgeBg}`}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          dir="rtl"
          className="absolute left-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
          style={{ top: '100%' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <span className="font-semibold text-gray-800 text-sm">الإشعارات</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium transition"
              >
                تحديد الكل كمقروء
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-400 text-sm">لا توجد إشعارات</div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className={`w-full text-right px-4 py-3 hover:bg-gray-50 transition flex items-start gap-3 ${n.isRead ? 'opacity-60' : ''}`}
                >
                  {!n.isRead && (
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                  )}
                  {n.isRead && <span className="mt-1.5 w-2 h-2 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{n.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                    <p className="text-[0.65rem] text-gray-400 mt-1">
                      {new Date(n.createdAt).toLocaleString('ar-SA', {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
